import { memo, useSyncExternalStore } from "react"
import {
  detectAllDuplicates,
  repairAllContainers,
  repairContainerIndexes,
} from "@/db/operations/indexRepair"

// ---------------------------------------------------------------------------
// Global toggle: press Ctrl+Shift+D to show/hide index debug badges
// ---------------------------------------------------------------------------

let _show = false
const listeners = new Set<() => void>()

function toggle() {
  _show = !_show
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function getSnapshot() {
  return _show
}

// Register keyboard shortcuts and console helpers (dev only)
if (import.meta.env.DEV) {
  document.addEventListener("keydown", (e) => {
    // Ctrl+Shift+D (or Cmd+Shift+D) — toggle debug badges
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "D") {
      e.preventDefault()
      toggle()
    }
    // Ctrl+Shift+R (or Cmd+Shift+R) — repair all duplicate indexes
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "R") {
      e.preventDefault()
      console.log("[IndexRepair] Scanning for duplicate indexes...")
      detectAllDuplicates().then((diagnostics) => {
        if (diagnostics.length === 0) {
          console.log("[IndexRepair] ✅ No duplicate indexes found!")
          return
        }
        console.table(
          diagnostics.map((d) => ({
            container: d.container,
            items: d.itemCount,
            duplicates: d.duplicateCount,
          })),
        )
        console.log("[IndexRepair] Repairing...")
        repairAllContainers().then((results) => {
          console.log("[IndexRepair] ✅ Done!")
          console.table(
            results.map((r) => ({
              container: r.container,
              items: r.itemCount,
              updated: r.updatedCount,
              ms: r.durationMs.toFixed(1),
            })),
          )
        })
      })
    }
  })

  // Expose on window for console access
  // Usage: __indexRepair.detect() or __indexRepair.repairAll()
  ;(window as unknown as Record<string, unknown>).__indexRepair = {
    detect: detectAllDuplicates,
    repairAll: repairAllContainers,
    repairContainer: repairContainerIndexes,
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function useShowDebugIndex() {
  return useSyncExternalStore(subscribe, getSnapshot)
}

interface DebugIndexBadgeProps {
  id?: string | null
  index?: number
}

export const DebugIndexBadge = memo(function DebugIndexBadge({
  id,
  index,
}: DebugIndexBadgeProps) {
  const show = useShowDebugIndex()
  if (!show) return null

  const shortId = id ? id.slice(-6) : "???"
  const idxStr = index != null ? index.toFixed(4) : "N/A"

  return (
    <span
      className="pointer-events-none absolute right-1 top-0 z-50 max-w-[200px] truncate rounded bg-black/80 px-1 py-0.5 font-mono text-[9px] leading-tight text-lime-400"
      title={`id: ${id}\nindex: ${index}`}
    >
      {shortId} | {idxStr}
    </span>
  )
})
